import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api-client.js';
import { useAuthStore } from '../stores/auth-store.js';

interface WebsiteData {
  id: string;
  subdomain: string;
  status: string;
  site_title: string;
  tagline?: string;
  about_text?: string;
  logo_url?: string;
  hero_image_url?: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  bg_color: string;
  text_color: string;
  contact_phone?: string;
  contact_email?: string;
  contact_address?: string;
  business_hours?: string;
  facebook_url?: string;
  instagram_url?: string;
  tiktok_url?: string;
  youtube_url?: string;
  google_maps_url?: string;
  show_financing: number;
  show_trade_in: number;
  show_contact_form: number;
  last_generated_at?: string;
  last_inventory_sync_at?: string;
  generation_version: number;
  total_views: number;
  total_leads: number;
  gen_log?: Array<{
    id: string;
    gen_type: string;
    status: string;
    pages_generated: number;
    vehicles_included: number;
    duration_ms: number;
    created_at: string;
  }>;
}

const COLOR_PRESETS = [
  { name: 'Ocean Blue', primary: '#3b82f6', secondary: '#10b981', accent: '#f59e0b', bg: '#0f172a' },
  { name: 'Crimson', primary: '#ef4444', secondary: '#f97316', accent: '#eab308', bg: '#18181b' },
  { name: 'Emerald', primary: '#10b981', secondary: '#06b6d4', accent: '#f59e0b', bg: '#064e3b' },
  { name: 'Purple Night', primary: '#8b5cf6', secondary: '#ec4899', accent: '#f59e0b', bg: '#1e1b4b' },
  { name: 'Slate Pro', primary: '#6366f1', secondary: '#14b8a6', accent: '#f59e0b', bg: '#0f172a' },
  { name: 'Dark Gold', primary: '#d97706', secondary: '#b45309', accent: '#fbbf24', bg: '#1c1917' },
];

export default function WebsiteBuilderPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'config' | 'preview' | 'leads' | 'log'>('config');

  const { data: websiteData, isLoading } = useQuery({
    queryKey: ['website'],
    queryFn: () => api.get<WebsiteData>('/website'),
  });

  const website = websiteData?.data;
  const hasWebsite = !!website?.id;

  return (
    <div className="page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Website Builder</h2>
        {website?.subdomain && (
          <a
            href={`https://${website.subdomain}.autocarpost.com`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-400 text-sm"
          >{website.subdomain}.autocarpost.com</a>
        )}
      </div>

      {isLoading && <p className="text-surface-500 text-center py-12">Loading...</p>}

      {!isLoading && !hasWebsite && (
        <CreateWebsiteForm onCreated={() => qc.invalidateQueries({ queryKey: ['website'] })} />
      )}

      {!isLoading && hasWebsite && (
        <>
          {/* Status Bar */}
          <div className="card p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`w-3 h-3 rounded-full ${
                website.status === 'published' ? 'bg-green-500' :
                website.status === 'generating' ? 'bg-yellow-500 animate-pulse' :
                website.status === 'error' ? 'bg-red-500' :
                'bg-surface-500'
              }`} />
              <span className="capitalize font-medium">{website.status}</span>
              {website.last_generated_at && (
                <span className="text-xs text-surface-500">
                  Last generated: {new Date(website.last_generated_at).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="flex gap-3 text-sm">
              <div className="text-center">
                <p className="font-bold text-brand-400">{website.total_views}</p>
                <p className="text-xs text-surface-500">Views</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-green-400">{website.total_leads}</p>
                <p className="text-xs text-surface-500">Leads</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-surface-300">v{website.generation_version}</p>
                <p className="text-xs text-surface-500">Version</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            {(['config', 'preview', 'leads', 'log'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 rounded-lg text-sm capitalize ${tab === t ? 'bg-brand-500 text-white' : 'bg-surface-800 text-surface-400'}`}
              >{t}</button>
            ))}
          </div>

          {tab === 'config' && <WebsiteConfigForm website={website} />}
          {tab === 'preview' && <WebsitePreview subdomain={website.subdomain} />}
          {tab === 'leads' && <WebsiteLeads />}
          {tab === 'log' && <GenerationLog log={website.gen_log ?? []} />}
        </>
      )}
    </div>
  );
}

// ── Create Website Form ────────────────────────────
function CreateWebsiteForm({ onCreated }: { onCreated: () => void }) {
  const dealership = useAuthStore((s) => s.dealership);
  const [form, setForm] = useState({
    site_title: dealership?.name ?? '',
    subdomain: dealership?.slug ?? '',
    tagline: '',
    about_text: '',
    contact_phone: '',
    contact_email: '',
  });

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post('/website', data),
    onSuccess: onCreated,
  });

  return (
    <div className="card p-6 border border-brand-500/30">
      <h3 className="text-lg font-semibold mb-4">Create Your Dealer Website</h3>
      <p className="text-surface-500 mb-6">Get a professional website at {form.subdomain || 'your-name'}.autocarpost.com in seconds.</p>

      <div className="space-y-4">
        <div>
          <label className="text-xs text-surface-500 block mb-1">Website Name</label>
          <input value={form.site_title} onChange={(e) => setForm({ ...form, site_title: e.target.value })} className="input" placeholder="Your Dealership Name" />
        </div>
        <div>
          <label className="text-xs text-surface-500 block mb-1">Subdomain</label>
          <div className="flex items-center">
            <input value={form.subdomain} onChange={(e) => setForm({ ...form, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} className="input rounded-r-none" placeholder="your-name" />
            <span className="bg-surface-700 border border-surface-600 border-l-0 rounded-r-lg px-3 py-3 text-sm text-surface-400">.autocarpost.com</span>
          </div>
        </div>
        <div>
          <label className="text-xs text-surface-500 block mb-1">Tagline</label>
          <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="input" placeholder="Quality vehicles at great prices" />
        </div>
        <div>
          <label className="text-xs text-surface-500 block mb-1">About (optional — we can AI-generate this)</label>
          <textarea value={form.about_text} onChange={(e) => setForm({ ...form, about_text: e.target.value })} className="input h-24" placeholder="Tell customers about your dealership..." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-surface-500 block mb-1">Phone</label>
            <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="input" placeholder="(555) 123-4567" />
          </div>
          <div>
            <label className="text-xs text-surface-500 block mb-1">Email</label>
            <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="input" placeholder="sales@dealer.com" />
          </div>
        </div>

        <button
          onClick={() => createMut.mutate(form)}
          disabled={!form.site_title || !form.subdomain || createMut.isPending}
          className="btn-primary w-full py-3 text-lg disabled:opacity-50"
        >{createMut.isPending ? 'Creating...' : 'Create Website'}</button>
      </div>
    </div>
  );
}

// ── Website Config Editor ────────────────────────────
function WebsiteConfigForm({ website }: { website: WebsiteData }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    site_title: website.site_title ?? '',
    tagline: website.tagline ?? '',
    about_text: website.about_text ?? '',
    primary_color: website.primary_color ?? '#3b82f6',
    secondary_color: website.secondary_color ?? '#10b981',
    accent_color: website.accent_color ?? '#f59e0b',
    bg_color: website.bg_color ?? '#0f172a',
    contact_phone: website.contact_phone ?? '',
    contact_email: website.contact_email ?? '',
    contact_address: website.contact_address ?? '',
    facebook_url: website.facebook_url ?? '',
    instagram_url: website.instagram_url ?? '',
    tiktok_url: website.tiktok_url ?? '',
    youtube_url: website.youtube_url ?? '',
    google_maps_url: website.google_maps_url ?? '',
    show_financing: website.show_financing,
    show_contact_form: website.show_contact_form,
  });

  const updateMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.patch('/website', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website'] }),
  });

  const generateMut = useMutation({
    mutationFn: () => api.post('/website/generate'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website'] }),
  });

  const syncMut = useMutation({
    mutationFn: () => api.post('/website/sync-inventory'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website'] }),
  });

  const unpublishMut = useMutation({
    mutationFn: () => api.post('/website/unpublish'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website'] }),
  });

  const aiAboutMut = useMutation({
    mutationFn: () => api.post<{ about_text: string }>('/website/generate-about', { site_title: form.site_title }),
    onSuccess: (res) => { if (res.data?.about_text) setForm(f => ({ ...f, about_text: res.data!.about_text })); },
  });

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => generateMut.mutate()} disabled={generateMut.isPending} className="btn-primary px-4 py-2 text-sm disabled:opacity-50">
          {generateMut.isPending ? 'Generating...' : 'Generate & Publish'}
        </button>
        <button onClick={() => syncMut.mutate()} disabled={syncMut.isPending} className="btn-secondary px-4 py-2 text-sm disabled:opacity-50">
          {syncMut.isPending ? 'Syncing...' : 'Sync Inventory'}
        </button>
        <button onClick={() => updateMut.mutate(form)} disabled={updateMut.isPending} className="btn-secondary px-4 py-2 text-sm disabled:opacity-50">
          {updateMut.isPending ? 'Saving...' : 'Save Settings'}
        </button>
        {website.status === 'published' && (
          <button onClick={() => unpublishMut.mutate()} className="text-sm px-4 py-2 bg-red-500/10 text-red-400 rounded-lg">
            Unpublish
          </button>
        )}
      </div>

      {/* Branding */}
      <div className="card p-4">
        <h3 className="font-semibold mb-3">Branding</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-surface-500 block mb-1">Site Title</label>
            <input value={form.site_title} onChange={(e) => setForm({ ...form, site_title: e.target.value })} className="input" />
          </div>
          <div>
            <label className="text-xs text-surface-500 block mb-1">Tagline</label>
            <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="input" />
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-surface-500">About Text</label>
            <button onClick={() => aiAboutMut.mutate()} disabled={aiAboutMut.isPending} className="text-xs text-brand-400 hover:underline">
              {aiAboutMut.isPending ? 'Generating...' : 'AI Generate'}
            </button>
          </div>
          <textarea value={form.about_text} onChange={(e) => setForm({ ...form, about_text: e.target.value })} className="input h-24" />
        </div>
      </div>

      {/* Colors */}
      <div className="card p-4">
        <h3 className="font-semibold mb-3">Theme Colors</h3>
        <div className="flex gap-2 mb-3 flex-wrap">
          {COLOR_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => setForm({ ...form, primary_color: preset.primary, secondary_color: preset.secondary, accent_color: preset.accent, bg_color: preset.bg })}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-800 hover:bg-surface-700 text-sm"
            >
              <div className="flex gap-1">
                <div style={{ background: preset.primary }} className="w-4 h-4 rounded-full" />
                <div style={{ background: preset.secondary }} className="w-4 h-4 rounded-full" />
                <div style={{ background: preset.accent }} className="w-4 h-4 rounded-full" />
              </div>
              {preset.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[
            ['Primary', 'primary_color'],
            ['Secondary', 'secondary_color'],
            ['Accent', 'accent_color'],
            ['Background', 'bg_color'],
          ].map(([label, key]) => (
            <div key={key}>
              <label className="text-xs text-surface-500 block mb-1">{label}</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={(form as any)[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-8 h-8 rounded cursor-pointer"
                />
                <input
                  value={(form as any)[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="input text-xs"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact & Social */}
      <div className="card p-4">
        <h3 className="font-semibold mb-3">Contact & Social</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-surface-500 block mb-1">Phone</label>
            <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="input" />
          </div>
          <div>
            <label className="text-xs text-surface-500 block mb-1">Email</label>
            <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-surface-500 block mb-1">Address</label>
            <input value={form.contact_address} onChange={(e) => setForm({ ...form, contact_address: e.target.value })} className="input" />
          </div>
          <div>
            <label className="text-xs text-surface-500 block mb-1">Facebook URL</label>
            <input value={form.facebook_url} onChange={(e) => setForm({ ...form, facebook_url: e.target.value })} className="input" placeholder="https://facebook.com/..." />
          </div>
          <div>
            <label className="text-xs text-surface-500 block mb-1">Instagram URL</label>
            <input value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} className="input" placeholder="https://instagram.com/..." />
          </div>
          <div>
            <label className="text-xs text-surface-500 block mb-1">TikTok URL</label>
            <input value={form.tiktok_url} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} className="input" placeholder="https://tiktok.com/@..." />
          </div>
          <div>
            <label className="text-xs text-surface-500 block mb-1">Google Maps URL</label>
            <input value={form.google_maps_url} onChange={(e) => setForm({ ...form, google_maps_url: e.target.value })} className="input" placeholder="https://goo.gl/maps/..." />
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="card p-4">
        <h3 className="font-semibold mb-3">Features</h3>
        <div className="space-y-2">
          {[
            { key: 'show_financing', label: 'Show Financing Page & Calculator' },
            { key: 'show_contact_form', label: 'Show Contact Form' },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={(form as any)[key] === 1}
                onChange={(e) => setForm({ ...form, [key]: e.target.checked ? 1 : 0 })}
                className="w-5 h-5 rounded"
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Preview ────────────────────────────────────────
function WebsitePreview({ subdomain }: { subdomain: string }) {
  const pages = ['index', 'inventory', 'about', 'contact', 'financing'];
  const [activePage, setActivePage] = useState('index');

  return (
    <div>
      <div className="flex gap-2 mb-3">
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => setActivePage(p)}
            className={`text-xs px-3 py-1 rounded capitalize ${activePage === p ? 'bg-brand-500 text-white' : 'bg-surface-800 text-surface-400'}`}
          >{p}</button>
        ))}
      </div>
      <div className="card overflow-hidden" style={{ height: '70vh' }}>
        <iframe
          src={`/v1/website/preview/${activePage}?subdomain=${subdomain}`}
          title="Website Preview"
          className="w-full h-full border-0"
          sandbox="allow-scripts"
        />
      </div>
    </div>
  );
}

// ── Leads ────────────────────────────────────────
function WebsiteLeads() {
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['website-leads'],
    queryFn: () => api.get<any[]>('/website/leads'),
  });

  const markRead = useMutation({
    mutationFn: (leadId: string) => api.patch(`/website/leads/${leadId}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-leads'] }),
  });

  const leads = data?.data ?? [];

  return (
    <div className="space-y-2">
      {leads.length === 0 && (
        <p className="text-center text-surface-500 py-8">No leads yet. Leads will appear here when visitors submit contact forms.</p>
      )}
      {leads.map((lead: any) => (
        <div key={lead.id} className={`card p-4 ${!lead.is_read ? 'border-l-4 border-brand-500' : ''}`}>
          <div className="flex justify-between items-start">
            <div>
              <p className="font-semibold">{lead.name}</p>
              <p className="text-sm text-surface-500">{lead.email} {lead.phone ? `| ${lead.phone}` : ''}</p>
              {lead.message && <p className="text-sm mt-2">{lead.message}</p>}
            </div>
            <div className="text-right">
              <span className="text-xs px-2 py-1 rounded-full bg-surface-700 text-surface-400 capitalize">{lead.lead_type}</span>
              <p className="text-xs text-surface-500 mt-1">{new Date(lead.created_at).toLocaleDateString()}</p>
              {!lead.is_read && (
                <button onClick={() => markRead.mutate(lead.id)} className="text-xs text-brand-400 mt-1">Mark read</button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Generation Log ────────────────────────────────
function GenerationLog({ log }: { log: any[] }) {
  return (
    <div className="space-y-2">
      {log.length === 0 && (
        <p className="text-center text-surface-500 py-8">No generation history yet.</p>
      )}
      {log.map((entry: any) => (
        <div key={entry.id} className="card p-4">
          <div className="flex justify-between items-center">
            <div>
              <span className={`text-xs px-2 py-1 rounded-full mr-2 ${
                entry.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                entry.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>{entry.status}</span>
              <span className="text-sm capitalize">{entry.gen_type?.replace('_', ' ')}</span>
            </div>
            <span className="text-xs text-surface-500">{new Date(entry.created_at).toLocaleString()}</span>
          </div>
          <div className="flex gap-4 mt-2 text-xs text-surface-500">
            <span>{entry.pages_generated} pages</span>
            <span>{entry.vehicles_included} vehicles</span>
            {entry.duration_ms && <span>{(entry.duration_ms / 1000).toFixed(1)}s</span>}
          </div>
          {entry.error_message && <p className="text-xs text-red-400 mt-1">{entry.error_message}</p>}
        </div>
      ))}
    </div>
  );
}
